import React from 'react';

/**
 * TrustBadges
 * 
 * Reusable trust & compliance badge components for:
 * - Award-compliant shift wages ✓
 * - Fair Work verified shifts 🔒
 * - Protected shift (escrow + verified) 🛡️
 * - Worker rights summary
 * - Employer compliance score
 */

export function AwardCompliantBadge({ wage, role, location, awardRate = 160 }) {
  const isCompliant = wage >= awardRate;
  
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
      isCompliant
        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
        : 'bg-amber-50 text-amber-700 border border-amber-200'
    }`}>
      {isCompliant ? '✓ Award+' : '⚠ Below Award'}
      <span className="text-[10px] font-normal text-neutral-600">
        ${wage}/hr
      </span>
    </div>
  );
}

export function FairWorkVerifiedBadge({ verified = true }) {
  if (!verified) return null;
  
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
      🔒 Fair Work Verified
    </div>
  );
}

export function ProtectedShiftBadge({ escrowConfirmed, fairWorkVerified }) {
  const isProtected = escrowConfirmed && fairWorkVerified;
  
  if (!isProtected) return null;
  
  return (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
      🛡️ Protected Shift
    </div>
  );
}

export function WorkerRightsSummary() {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
      <h4 className="text-sm font-semibold text-blue-900">Your Worker Rights</h4>
      <ul className="text-sm text-blue-800 space-y-2">
        <li className="flex gap-2">
          <span className="text-base">💵</span>
          <span><strong>Fair pay:</strong> Minimum award wage guaranteed</span>
        </li>
        <li className="flex gap-2">
          <span className="text-base">🔐</span>
          <span><strong>Payment secure:</strong> Escrow held until shift complete</span>
        </li>
        <li className="flex gap-2">
          <span className="text-base">⏱️</span>
          <span><strong>No surprises:</strong> Pay & hours locked before you start</span>
        </li>
        <li className="flex gap-2">
          <span className="text-base">📸</span>
          <span><strong>Verified employers:</strong> All companies verified by ShiftMatch</span>
        </li>
      </ul>
    </div>
  );
}

export function EmployerComplianceScore({ score = 95, percentShiftsAtAwardRate = 98 }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-neutral-900">Compliance Score</h4>
        <span className={`text-lg font-bold ${score >= 90 ? 'text-emerald-600' : score >= 75 ? 'text-amber-600' : 'text-rose-600'}`}>
          {score}%
        </span>
      </div>
      <div className="space-y-2 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-neutral-600">Award wage compliance</span>
          <span className="font-semibold">{percentShiftsAtAwardRate}%</span>
        </div>
        <div className="w-full h-2 bg-neutral-200 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500" style={{ width: `${percentShiftsAtAwardRate}%` }} />
        </div>
      </div>
      {score >= 90 && (
        <p className="text-[11px] text-emerald-700 bg-emerald-50 px-2 py-1.5 rounded">
          ✓ Excellent record — workers trust your shifts
        </p>
      )}
    </div>
  );
}

export function VerificationChecklist({ userType = 'worker' }) {
  const workerSteps = [
    { id: 'id', label: 'ID verified', icon: '🪪', done: true },
    { id: 'bank', label: 'Bank details added', icon: '🏦', done: true },
    { id: 'phone', label: 'Phone verified', icon: '📱', done: false },
  ];

  const employerSteps = [
    { id: 'abn', label: 'ABN verified', icon: '📋', done: true },
    { id: 'business', label: 'Business registered', icon: '🏢', done: true },
    { id: 'insurance', label: 'Insurance on file', icon: '📄', done: false },
  ];

  const steps = userType === 'worker' ? workerSteps : employerSteps;

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-neutral-900 mb-3">Verification Progress</h4>
      {steps.map(step => (
        <div key={step.id} className="flex items-center gap-3 p-2.5 bg-neutral-50 rounded-lg">
          <span className="text-lg">{step.icon}</span>
          <span className="text-sm text-neutral-700 flex-1">{step.label}</span>
          {step.done ? (
            <span className="text-emerald-600 font-semibold text-xs">✓</span>
          ) : (
            <span className="text-neutral-400 text-xs">○</span>
          )}
        </div>
      ))}
    </div>
  );
}

export function ShiftComplianceIndicators({ shift }) {
  return (
    <div className="flex flex-wrap gap-2">
      <AwardCompliantBadge wage={shift.wage} role={shift.role} location={shift.location} />
      {shift.fair_work_verified && <FairWorkVerifiedBadge verified={true} />}
      {shift.escrow_confirmed && shift.fair_work_verified && (
        <ProtectedShiftBadge escrowConfirmed={true} fairWorkVerified={true} />
      )}
    </div>
  );
}
